import { api } from "@/features/auth/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Store = {
    id: string;
    name: string;
    code: string;
    address_line1: string;
    address_line2: string;
    city: string;
    state: string;
    pincode: string;
    phone: string;
    geo: { lat: number; lng: number };
    service_radius_km: number;
    is_active: boolean;
    distance_km?: number;
};

export type StoreWrite = Omit<Store, "id" | "distance_km">;

export type TeamMember = {
    id: string;
    email: string;
    full_name: string;
    phone: string;
    role: string;
    store_id: string | null;
    is_blocked: boolean;
    salary_rate_per_hour: number;
    created_at: string;
};

export type ActivityRow = {
    id: string;
    actor_id: string;
    actor_email: string;
    actor_role: string;
    action: string;
    target: string;
    metadata: Record<string, unknown>;
    at: string;
};

export type Settlement = {
    id: string;
    rider_id: string;
    store_id: string | null;
    amount: number;
    note: string;
    settled_by: string;
    settled_by_email: string;
    at: string;
};

export type AttendanceRow = {
    id: string;
    staff_id: string;
    store_id: string | null;
    date: string;
    hours: number;
    rate_per_hour: number;
    salary: number;
    notes: string;
    at: string;
};

export type PayrollRow = {
    staff_id: string;
    full_name: string;
    role: string;
    email: string;
    total_hours: number;
    total_salary: number;
    shifts: number;
};

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const opsApi = {
    // Stores
    listPublicStores: async (lat?: number, lng?: number) => {
        const { data } = await api.get<{ items: Store[]; total: number }>("/stores", {
            params: { lat, lng },
        });
        return data;
    },
    nearestStore: async (lat: number, lng: number) => {
        const { data } = await api.get<Store>("/stores/nearest", { params: { lat, lng } });
        return data;
    },
    listStores: async () => {
        const { data } = await api.get<{ items: Store[]; total: number }>(
            "/admin/stores",
        );
        return data;
    },
    createStore: async (
        payload: StoreWrite & {
            admin?: {
                full_name: string;
                email: string;
                phone: string;
                password: string;
            };
        },
    ) => {
        const { data } = await api.post<Store>("/admin/stores", payload);
        return data;
    },
    updateStore: async (id: string, payload: StoreWrite) => {
        const { data } = await api.put<Store>(`/admin/stores/${id}`, payload);
        return data;
    },
    deleteStore: async (id: string) => {
        await api.delete(`/admin/stores/${id}`);
    },

    // Team
    listTeam: async (params?: { role?: string; store_id?: string; page?: number; page_size?: number }) => {
        const { data } = await api.get<{ items: TeamMember[]; total: number }>(
            "/operations/team",
            { params },
        );
        return data;
    },
    createTeamMember: async (payload: {
        role: string;
        full_name: string;
        email: string;
        phone: string;
        password: string;
        store_id?: string | null;
        vehicle_type?: string;
        vehicle_number?: string;
        salary_rate_per_hour?: number;
    }) => {
        const { data } = await api.post<TeamMember>("/operations/team", payload);
        return data;
    },
    updateTeamMember: async (
        id: string,
        payload: Partial<{
            full_name: string;
            phone: string;
            store_id: string | null;
            is_blocked: boolean;
            salary_rate_per_hour: number;
        }>,
    ) => {
        const { data } = await api.patch<TeamMember>(`/operations/team/${id}`, payload);
        return data;
    },
    deleteTeamMember: async (id: string) => {
        await api.delete(`/operations/team/${id}`);
    },

    // Activity
    listActivity: async (page = 1, page_size = 50) => {
        const { data } = await api.get<{ items: ActivityRow[]; total: number }>(
            "/operations/activity",
            { params: { page, page_size } },
        );
        return data;
    },

    // Settlements
    listSettlements: async (rider_id?: string) => {
        const { data } = await api.get<{ items: Settlement[]; total: number }>(
            "/operations/settlements",
            { params: rider_id ? { rider_id } : {} },
        );
        return data;
    },
    createSettlement: async (payload: { rider_id: string; amount: number; note?: string }) => {
        const { data } = await api.post<Settlement>("/operations/settlements", payload);
        return data;
    },

    // Attendance + Payroll
    listAttendance: async (staff_id?: string) => {
        const { data } = await api.get<{ items: AttendanceRow[]; total: number }>(
            "/operations/attendance",
            { params: staff_id ? { staff_id } : {} },
        );
        return data;
    },
    createAttendance: async (payload: {
        staff_id: string;
        date: string;
        hours: number;
        notes?: string;
    }) => {
        const { data } = await api.post<AttendanceRow>(
            "/operations/attendance",
            payload,
        );
        return data;
    },
    payrollSummary: async () => {
        const { data } = await api.get<{ items: PayrollRow[]; total: number }>(
            "/operations/payroll/summary",
        );
        return data;
    },
};
